import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { applyColor } from '~src/style/ColorService';

import { cssClass, fillStyle, flexColumnStyle } from '../../../style';

const cssPrefix = 'TaskDetails';

export namespace TaskDetailsSpaceStyles {
  // Flex column filling the subplace area so the metadata block and history section split the height,
  // making the grid body the single scroll region (not the page).
  export const ContentFill = cssClass(cssPrefix, 'content-fill', fillStyle, flexColumnStyle);

  // Task-action bar (Epic 264 stretch): left-aligned row that sits directly under the "Task Details"
  // title (inside the masthead, so it inherits the masthead's horizontal inset and lines up with the
  // title) and above the header divider (Figma). Holds the "Run Now" button; never scrolls or shrinks.
  export const ActionsRow = cssClass(cssPrefix, 'actions-row', {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: TabStyles.Spacing.MediumSpace,
    flexShrink: 0,
    marginTop: TabStyles.Spacing.MediumSpace,
  });

  // Full-width grey rule separating the header block (title + actions) from the page body (metadata +
  // history), matching the Figma "grey line divider" beneath the Task Details title.
  export const HeaderDivider = cssClass(cssPrefix, 'header-divider', {
    flexShrink: 0,
    height: 1,
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L3)}`,
  });

  // Task metadata section (PRD 2.2.5 / 2.2.6 / 2.2.7): fixed height, may scroll horizontally but never vertically.
  // Horizontal padding insets the content from the left panel to match the masthead (Figma).
  // Symmetric vertical padding gives the metadata block breathing room instead of squishing it
  // against the divider above.
  export const MetadataSection = cssClass(cssPrefix, 'metadata-section', {
    flexShrink: 0,
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingTop: TabStyles.Spacing.LargeSpace,
    paddingBottom: TabStyles.Spacing.LargeSpace,
    paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
    paddingRight: TabStyles.Spacing.ExtraLargeSpace,
  });

  // Placeholder for the metadata grid. Kept obvious so the integration point is clear.
  export const MetadataPlaceholder = cssClass(cssPrefix, 'metadata-placeholder', {
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
    fontSize: '14px',
  });

  // History section: heading + grid region; it doesn't scroll itself (the DataGrid owns the single
  // scroll body). No horizontal padding so the grid is flush with the space edges; only the heading is inset.
  export const HistorySection = cssClass(cssPrefix, 'history-section', flexColumnStyle, {
    flex: 1,
    minHeight: 0, // allow the flex child to shrink so the inner container's overflow scrolls, not the page
    overflow: 'hidden',
    paddingTop: TabStyles.Spacing.LargeSpace,
  });

  // Heading inset to align with the page title/metadata (the grid is flush-left). No border here —
  // the DataGrid draws the divider under the heading itself.
  export const HistoryHeading = cssClass(cssPrefix, 'history-heading', {
    fontSize: '15px',
    fontWeight: 'normal',
    marginBottom: TabStyles.Spacing.MediumSpace,
    paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
    paddingRight: TabStyles.Spacing.ExtraLargeSpace,
  });

  // Flex region that fills the space below the history heading. The job table renders through the
  // shared `DataGrid` widget, which owns its own single virtualized scroll body + sticky header.
  export const HistoryGridRegion = cssClass(cssPrefix, 'history-grid-region', flexColumnStyle, {
    flex: 1,
    minHeight: 0,
  });

  // Positioning context for the absolutely-filled grid (a bounded container for its viewport, like
  // ListView); flex-fills the region so the grid body is the only scroll region.
  export const HistoryGridBox = cssClass(cssPrefix, 'history-grid-box', {
    flex: 1,
    minHeight: 0,
    position: 'relative',
  });

  // DataGrid root: fills HistoryGridBox and matches the list-view typography. The autoscroll target
  // (aria-selected) is re-tinted with the neutral hover token and faded out.
  export const HistoryGrid = cssClass(cssPrefix, 'history-grid', {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    fontSize: '14px',
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Colors.F9),

    $nest: {
      '.tb-react-dg-hcell': {
        fontSize: '12px',
        color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
      },
      // Status Message wrap/clamp/centering lives entirely on the cell's own inline styles
      // (StatusMessageCell's styleOverrides + clamp span in JobHistoryTable.tsx), so no grid-cell
      // override is needed here — avoiding a production-styling dependency on the test-id selector.
      // The shared DataGrid root sets `user-select: none`, which blocks copying values such as the job
      // LUID or the status message. Re-enable text selection on body cells only (headers stay unselectable).
      '.tb-react-dg-bcell': {
        userSelect: 'text',
      },
      '.tb-react-dg-brow[aria-selected="true"], .tb-react-dg-bcell[aria-selected="true"]': {
        backgroundColor: applyColor(DesignTokens.color.component.background.neutral.default.hover, TabStyles.Colors.L2),
        transition: 'background-color 1s ease-out',
      },
    },
  });

  // Off-screen but screen-reader-accessible container for polite live-region announcements
  // (autoscroll target + pagination). The standard clip-and-offset visually-hidden recipe.
  export const VisuallyHidden = cssClass(cssPrefix, 'visually-hidden', {
    position: 'absolute',
    width: '1px',
    height: '1px',
    margin: '-1px',
    padding: 0,
    border: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  });

  // Status indicator (Figma: colored status glyph + label, replacing the earlier pill badge).
  // The design-system Status* icons carry their own semantic color, so the text stays default.
  // Font size is inherited from the grid body (14px) to match the shared list-view typography.
  export const StatusIndicator = cssClass(cssPrefix, 'status-indicator', {
    display: 'inline-flex',
    alignItems: 'center',
    gap: TabStyles.Spacing.SmallSpace,
    whiteSpace: 'nowrap',

    $nest: {
      '& svg': {
        flexShrink: 0,
      },
    },
  });

  // Placeholder/empty state for job run history table. Inset to align with the heading (the grid is
  // flush, but a prose message reads better indented with the rest of the section content).
  export const HistoryPlaceholder = cssClass(cssPrefix, 'history-placeholder', {
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
    fontSize: '14px',
    paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
    paddingRight: TabStyles.Spacing.ExtraLargeSpace,
  });

  export const HistoryLoadState = cssClass(cssPrefix, 'history-load-state', flexColumnStyle, {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
    fontSize: '13px',
    paddingTop: TabStyles.Spacing.SmallSpace,
    paddingLeft: TabStyles.Spacing.ExtraLargeSpace,
    paddingRight: TabStyles.Spacing.ExtraLargeSpace,
  });

  // Centered container for the loading spinner, error message, and empty state.
  export const CenteredState = cssClass(cssPrefix, 'centered-state', flexColumnStyle, {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  });

  export const StateMessage = cssClass(cssPrefix, 'state-message', {
    marginBottom: TabStyles.Spacing.LargeSpace,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
  });

  // Top metadata layout (Figma): the field groups are laid out in three columns across the
  // width of the page. Columns hold their minimum size and let the section scroll horizontally
  // (MetadataSection owns overflowX) on narrow viewports rather than crushing the labels.
  export const MetadataColumns = cssClass(cssPrefix, 'metadata-columns', {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))',
    columnGap: TabStyles.Spacing.ExtraLargeSpace,
    rowGap: TabStyles.Spacing.LargeSpace,
    alignItems: 'start',
    margin: 0,
  });

  // Task metadata grid (S05-b): label/value rows laid out as two columns within one column group.
  // A roomier row gap keeps the rows from looking squished, and centering each row aligns the
  // label with its value vertically (matters when a value wraps to more than one line).
  export const MetadataGrid = cssClass(cssPrefix, 'metadata-grid', {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    rowGap: TabStyles.Spacing.MediumSpace,
    columnGap: TabStyles.Spacing.LargeSpace,
    alignContent: 'start',
    alignItems: 'center',
    margin: 0,
  });

  // Each label/value pair spans the two grid columns (contents so dt/dd land in the grid tracks).
  export const MetadataRow = cssClass(cssPrefix, 'metadata-row', {
    display: 'contents',
  });

  export const MetadataLabel = cssClass(cssPrefix, 'metadata-label', {
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Colors.F9),
    fontSize: '14px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  });

  export const MetadataValue = cssClass(cssPrefix, 'metadata-value', {
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Colors.F9),
    fontSize: '14px',
    wordBreak: 'break-word',
  });

  // Grayed dash shown for null/empty fields (PRD null-handling contract).
  export const MetadataValueEmpty = cssClass(cssPrefix, 'metadata-value-empty', {
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
    fontSize: '14px',
  });

  // Task Not Found empty state container (Epic 264).
  export const NotFoundContainer = cssClass(cssPrefix, 'not-found-container', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: TabStyles.Spacing.MediumSpace,
  });

  // Task Not Found heading.
  export const NotFoundHeading = cssClass(cssPrefix, 'not-found-heading', {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Colors.F9),
  });

  // Task Not Found description text.
  export const NotFoundDescription = cssClass(cssPrefix, 'not-found-description', {
    fontSize: '14px',
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
    margin: 0,
    maxWidth: '300px',
    textAlign: 'center',
  });
}
